import { motion } from "framer-motion";
import {
  ArrowRight,
  FileSearch,
  ListChecks,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react";
import aiAppliedCard from "../../../assets/images/home/product/ai-applied-card.webp";
import aiInterview from "../../../assets/images/home/product/ai-interview.webp";

type Step = { icon: LucideIcon; title: string; description: string };

const STEPS: Step[] = [
  {
    icon: FileSearch,
    title: "AI CV parsing",
    description:
      "Every CV is read and structured automatically — skills, experience and education, ready to compare.",
  },
  {
    icon: ListChecks,
    title: "TalentHunt",
    description:
      "AI ranks applicants against your job post, so the strongest matches rise to the top.",
  },
  {
    icon: Video,
    title: "AI Interview",
    description:
      "Candidates answer on camera, one question at a time, whenever suits them. You get a scored summary.",
  },
];

const ease = [0.16, 1, 0.3, 1] as const;

const AiRecruitment = () => (
  <section className="relative overflow-hidden border-y border-gray-100 bg-gray-50/70 px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
    <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[620px] rounded-full bg-theme-primary/10 blur-[120px]" />

    <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      {/* Copy */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="text-sm font-semibold uppercase tracking-wider text-theme-primary">
          AI Recruitment
        </p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Hire the right people, faster
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-gray-500">
          From the first CV to the final interview, AI handles the screening so
          your team can focus on the decision.
        </p>

        <ol className="relative mt-10 space-y-7">
          <span
            aria-hidden
            className="absolute bottom-3 left-[17px] top-3 w-px bg-gradient-to-b from-theme-primary/50 via-gray-200 to-gray-200"
          />
          {STEPS.map(({ icon: Icon, title, description }) => (
            <li key={title} className="relative flex gap-4">
              <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-theme-primary">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <div className="pt-1.5">
                <h3 className="text-[15px] font-semibold text-gray-900">
                  {title}
                </h3>
                <p className="mt-1 text-[15px] leading-relaxed text-gray-500">
                  {description}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-theme-primary" />
            Pay per use on Premium & Enterprise
          </span>
          <span className="hidden text-gray-300 sm:inline">·</span>
          <button
            onClick={() =>
              document
                .getElementById("pricing")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center gap-1 font-medium text-theme-primary hover:underline hover:underline-offset-4"
          >
            See AI pricing
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </motion.div>

      {/* Product */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.1, ease }}
        className="relative pb-24 sm:pb-28"
      >
        <figure>
          <img
            src={aiInterview}
            alt="A candidate answering an AI interview question in HRx"
            width={1600}
            height={1392}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full rounded-[1.75rem] shadow-[0_30px_60px_-30px_rgba(15,23,42,0.5)] ring-1 ring-gray-900/10"
          />
        </figure>

        {/* Candidate's view of the same application */}
        <div className="absolute -left-4 bottom-0 w-[86%] sm:-left-10 sm:w-[58%]">
          <p className="mb-2 ml-1 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200">
            <span className="h-1.5 w-1.5 rounded-full bg-theme-primary" />
            Candidates track every stage
          </p>
          <img
            src={aiAppliedCard}
            alt="A candidate's application in HRx, currently at the interview stage"
            width={900}
            height={387}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full rounded-2xl shadow-[0_24px_50px_-24px_rgba(15,23,42,0.45)]"
          />
        </div>
      </motion.div>
    </div>
  </section>
);

export default AiRecruitment;
